import {
  BeneficiaryAggregateBooleanDimension,
  BeneficiaryAggregateDateDimension,
  BeneficiaryAggregateEnumDimension,
  BeneficiaryAggregateMultiselectDimension,
  BeneficiaryAggregateNumberDimension,
  BeneficiaryInsightDimension,
  BeneficiaryInsightMetric,
  BeneficiaryInsightWidgetDTO,
  BeneficiaryTimeSeriesDimension,
} from '@repo/shared/dtos/beneficiary'
import {
  StructuredDataSchemaDTO,
  StructuredDataSchemaItem,
} from '@repo/shared/dtos/structured-data'

import { CoreFormType } from './types'

const ALLOWED_STRUCTURED_DATA_TYPES_FOR_DIMENSIONS = [
  'date',
  'enum',
  'number',
  'boolean',
  'multiselect',
]

export function buildDimension(schemaItem: StructuredDataSchemaItem): BeneficiaryInsightDimension {
  switch (schemaItem.type) {
    case 'date':
      return {
        key: schemaItem.id,
        buckets: [],
      } satisfies BeneficiaryAggregateDateDimension
    case 'enum':
      return {
        key: schemaItem.id,
        buckets: schemaItem.enumValues.map((value) => ({
          label: value,
          values: [value],
        })),
      } satisfies BeneficiaryAggregateEnumDimension
    case 'number':
      return {
        key: schemaItem.id,
        buckets: [],
      } satisfies BeneficiaryAggregateNumberDimension
    case 'boolean':
      return {
        key: schemaItem.id,
        buckets: [
          { label: '✅', value: true }, // TODO: translate
          { label: '❌', value: false },
        ],
      } satisfies BeneficiaryAggregateBooleanDimension
    case 'multiselect':
      return {
        key: schemaItem.id,
        buckets: [],
      } satisfies BeneficiaryAggregateMultiselectDimension
    default:
      throw new Error(`Unsupported structured data type: ${schemaItem.type}`)
  }
}

export function getAllowedStructuredDataTypes(
  dimensions: BeneficiaryInsightDimension[],
  structuredDataSchema: StructuredDataSchemaDTO,
): StructuredDataSchemaItem[] {
  return structuredDataSchema.filter(
    (item) =>
      ALLOWED_STRUCTURED_DATA_TYPES_FOR_DIMENSIONS.includes(item.type) &&
      !dimensions.some((dimension) => dimension.key === item.id),
  )
}

export function buildDefaultValues(
  widget: BeneficiaryInsightWidgetDTO | null,
  structuredDataSchema: StructuredDataSchemaDTO,
): CoreFormType {
  const dimensions = widget?.dimensions ?? []
  return {
    title: widget?.title ?? '',
    description: widget?.description ?? '',
    type: widget?.type ?? 'kpi',
    filters: widget?.filters ?? [],
    metric:
      widget?.metrics[0] ??
      ({
        key: '*',
        operation: 'count',
      } satisfies BeneficiaryInsightMetric),
    dimensions,
    dimensionModes: [
      {
        date: getDateMode(dimensions[0], structuredDataSchema),
        number: getNumberMode(dimensions[0], structuredDataSchema),
      },
      {
        date: getDateMode(dimensions[1], structuredDataSchema),
        number: getNumberMode(dimensions[1], structuredDataSchema),
      },
    ],
  }
}

function getNumberMode(
  dimension: BeneficiaryInsightWidgetDTO['dimensions'][number] | undefined,
  structuredDataSchema: StructuredDataSchemaDTO,
): CoreFormType['dimensionModes'][number]['number'] {
  if (!isNumberDimension(dimension, structuredDataSchema)) return null
  if (dimension.buckets.some((bucket) => !!bucket.min && !!bucket.max)) return 'range'
  return 'simple'
}

function getDateMode(
  dimension: BeneficiaryInsightWidgetDTO['dimensions'][number] | undefined,
  structuredDataSchema: StructuredDataSchemaDTO,
): CoreFormType['dimensionModes'][number]['date'] {
  if (!isDateDimension(dimension, structuredDataSchema)) return null

  if (isTimeSeries(dimension)) return 'timeseries'
  if (dimension.buckets.some((bucket) => !!bucket.min && !!bucket.max)) return 'range'
  return 'simple'
}

function isTimeSeries(
  dimension: BeneficiaryInsightDimension,
): dimension is BeneficiaryTimeSeriesDimension {
  return 'timeSeries' in dimension
}

function isDateDimension(
  dimension: BeneficiaryInsightDimension | undefined,
  structuredDataSchema: StructuredDataSchemaDTO,
): dimension is BeneficiaryAggregateDateDimension {
  if (!dimension) return false
  const dimensionSchema = structuredDataSchema.find((item) => item.id === dimension.key)
  if (!dimensionSchema) return false
  return dimensionSchema.type === 'date'
}

function isNumberDimension(
  dimension: BeneficiaryInsightDimension | undefined,
  structuredDataSchema: StructuredDataSchemaDTO,
): dimension is BeneficiaryAggregateNumberDimension {
  if (!dimension) return false
  const dimensionSchema = structuredDataSchema.find((item) => item.id === dimension.key)
  if (!dimensionSchema) return false
  return dimensionSchema.type === 'number'
}
